/**
 * Maps raw API error strings (mostly English from Django/DRF) to ui namespace keys.
 * Unknown messages get a localized wrapper so the UI stays in the user's language.
 */
import { mapAuthApiErrorMessage } from '../../authentication/utils/mapAuthApiErrorMessage';

/** Lowercase trimmed English → i18n key under ui namespace */
const KNOWN_STRING_TO_I18N_KEY = new Map([
  ['not found.', 'backendErrors.drNotFound'],
  ['not found', 'backendErrors.drNotFound'],
  ['you do not have permission to perform this action.', 'backendErrors.drPermissionDenied'],
  ['authentication credentials were not provided.', 'backendErrors.drAuthenticationRequired'],
  ['method "get" not allowed.', 'backendErrors.drMethodNotAllowed'],
  ['method "post" not allowed.', 'backendErrors.drMethodNotAllowed'],
  ['method "put" not allowed.', 'backendErrors.drMethodNotAllowed'],
  ['method "patch" not allowed.', 'backendErrors.drMethodNotAllowed'],
  ['method "delete" not allowed.', 'backendErrors.drMethodNotAllowed'],
  ['method not allowed.', 'backendErrors.drMethodNotAllowed'],
  ['invalid token.', 'backendErrors.drInvalidToken'],
  ['given token not valid for any token type', 'backendErrors.drInvalidToken'],
  ['token is invalid or expired', 'backendErrors.drInvalidToken'],
  ['no active account found with the given credentials', 'backendErrors.drInvalidCredentials'],
  ['you cannot delete your own account.', 'settingsPages.users.cannotDeleteSelf'],
  ['this field is required.', 'backendErrors.djangoFieldRequired'],
  ['this field may not be blank.', 'backendErrors.djangoFieldBlank'],
  ['this field may not be null.', 'backendErrors.djangoFieldNull'],
  ['enter a valid email address.', 'backendErrors.djangoInvalidEmail'],
  ['a valid integer is required.', 'backendErrors.djangoInvalidInteger'],
  ['a valid number is required.', 'backendErrors.djangoInvalidNumber'],
]);

function translatePlainString(raw, t) {
  if (raw == null || typeof raw !== 'string') return raw;
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  const authMapped = mapAuthApiErrorMessage(trimmed, t);
  if (authMapped !== trimmed) return authMapped;

  const key = KNOWN_STRING_TO_I18N_KEY.get(trimmed.toLowerCase());
  if (key) {
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }

  return t('backendErrors.unknownServerMessage', { message: trimmed });
}

/**
 * Handles "field: message" lines from DRF validation — translates the message part when known.
 */
function translateSegment(segment, t) {
  const s = segment.trim();
  if (!s) return s;

  const colon = s.indexOf(': ');
  if (colon > 0) {
    const field = s.slice(0, colon);
    const rest = s.slice(colon + 2).trim();
    const translatedRest = translatePlainString(rest, t);
    if (translatedRest !== rest) return `${field}: ${translatedRest}`;
  }

  return translatePlainString(s, t);
}

/**
 * Translates a full API error body string; segments are usually joined with "; ".
 */
export function translateAggregateBackendMessage(raw, t) {
  if (raw == null || typeof raw !== 'string') return raw;
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;

  if (trimmed.includes('; ')) {
    return trimmed
      .split('; ')
      .map((part) => translateSegment(part, t))
      .join('; ');
  }

  return translateSegment(trimmed, t);
}

/**
 * If the API returns a stable machine code, translate via backendErrors.codes.<code>.
 */
export function translateBackendErrorCode(code, t) {
  if (code == null || typeof code !== 'string' || !code.trim()) return null;
  const key = `backendErrors.codes.${code.trim()}`;
  const translated = t(key);
  return translated !== key ? translated : null;
}
